import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
  KeyboardEventHandler
} from 'react';
import {useHistory} from 'react-router-dom';
import Box from '@mui/material/Box';
import {Button, CartIcon, Stack, Typography} from '@customink/pigment-react';
import {useGetStoreDetailsQuery, useGetCartQuery} from '../../api/storesApi';
import {MobileContext} from '../../containers/MobileProvider';
import {useAppSelector, useShouldForwardToPDP} from '../../hooks';
import services from '../../api/remoteServices';
import AccountDropdown from '../AccountDropdown';
import {selectAdmin, selectAuthorized} from '../../slices/userSlice';
import './index.scss';

type StoreHeaderProps = {
  storeSlug: string;
  preview: boolean;
  skipFetchProducts?: boolean;
};

const textColor = (color: string) =>
  color === 'White' ? 'var(--color-white)' : 'var(--color-slate)';
const position = (preview: boolean) => !preview && 'fixed';
const borderBottom = (isTop: boolean) => !isTop && '2px solid #ededed';

const StoreHeader: React.FC<StoreHeaderProps> = ({
  storeSlug,
  preview,
  skipFetchProducts
}: StoreHeaderProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);

  const mobile = useContext(MobileContext);
  const admin = useAppSelector(selectAdmin);
  const authorized = useAppSelector(selectAuthorized);
  const isOrganizerView = admin || authorized;

  const marginBottom = () => {
    if (preview) return '';

    return mobile && isOrganizerView ? '8.25rem' : '5rem';
  };

  const {data, isSuccess: isCartSuccess} = useGetCartQuery(storeSlug);
  const details = useAppSelector(state => state.stores.storeDetails);
  const localImages = useAppSelector(state => state.stores.localImages);
  const showLogo = details?.headerLogoType === 'logo';
  const logoImageUrl = localImages.logo || details?.logoImageUrl;
  const [isTop, setIsTop] = useState(true);

  const validateIsPageOnTop = (scrollY: number) => {
    return scrollY === 0 ? setIsTop(true) : setIsTop(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      validateIsPageOnTop(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  const stickyNavbar = (isPreview: StoreHeaderProps['preview']) => {
    return {
      position: position(isPreview),
      marginBottom: marginBottom(),
      borderBottom: borderBottom(isTop)
    };
  };

  const styles = {
    header: {
      marginBottom: marginBottom()
    },
    container: {
      backgroundColor: details?.bannerColor,
      color: textColor(details?.textColor),
      ...stickyNavbar(preview)
    } as React.CSSProperties
  };

  const StoreBrand = (): ReactElement => {
    const history = useHistory();
    const shouldForwardToPDP = useShouldForwardToPDP({skip: skipFetchProducts});
    const onStoreNameClick = useCallback(() => {
      if (shouldForwardToPDP === false) history.push(`/${storeSlug}`);
    }, [shouldForwardToPDP, history, storeSlug]);
    const onStoreNameKeydown = useCallback<
      KeyboardEventHandler<HTMLDivElement>
    >(
      e => {
        if (e.key === 'Enter') onStoreNameClick();
      },
      [onStoreNameClick]
    );
    return details ? (
      <div className="store-layout-header-logo">
        {showLogo && logoImageUrl && (
          <img
            className="store-layout-header-logo-image"
            src={logoImageUrl}
            alt="Logo"
          />
        )}
        {!showLogo && details?.name ? (
          <div
            role="link"
            tabIndex={0}
            className="store-layout-header-store-link"
            onKeyDown={onStoreNameKeydown}
            onClick={onStoreNameClick}>
            <Typography className="store-layout-header-title">
              {details.name}
            </Typography>
          </div>
        ) : null}
      </div>
    ) : (
      <></>
    );
  };

  const ManageButton: React.FC = () => {
    const manageUrl = `/go/${storeSlug}/manage/dashboard`;

    return isOrganizerView ? (
      <Button
        className="store-layout-header-manage"
        variant="secondary"
        href={manageUrl}
        target="_blank"
        rel="noreferrer">
        Go To Dashboard
      </Button>
    ) : (
      <></>
    );
  };

  const Cart = (): ReactElement => {
    const cartUrl = `${services.stores}/${storeSlug}/cart`;
    const CartItems = useMemo((): string => {
      if (!isCartSuccess || preview) return '';
      const {quantity} = data?.cart;
      return quantity ? `${quantity} ${quantity < 2 ? 'item' : 'items'}` : '';
    }, [data]);

    return (
      <Button
        className="store-layout-header-cart"
        size="medium"
        aria-label="cart"
        href={cartUrl}
        target="_blank"
        rel="noreferrer"
        startIcon={
          <CartIcon
            fontSize="inherit"
            className="store-layout-header-cart-image"
          />
        }>
        {CartItems}
      </Button>
    );
  };

  return details ? (
    <Box className="store-layout-header" style={styles.header}>
      <Stack className="store-layout-header-container" style={styles.container}>
        <div className="store-layout-header-inner">
          <div className="header-item">
            <StoreBrand />
          </div>
          {!mobile ? (
            <div className="header-item">
              <ManageButton />
            </div>
          ) : null}
          <div className="header-item">
            <Stack
              alignItems="center"
              justifyContent="space-between"
              direction="row"
              className="store-layout-header-menu">
              <AccountDropdown storeSlug={storeSlug} />
              <Cart />
            </Stack>
          </div>
        </div>
        {mobile ? (
          <div className="mobileManageButton-container">
            <ManageButton />
          </div>
        ) : null}
      </Stack>
    </Box>
  ) : (
    <></>
  );
};

StoreHeader.defaultProps = {
  skipFetchProducts: false
};

export default StoreHeader;
